/**
 * Market Hours Utility
 * Determines if futures markets (ES, NQ) are open
 *
 * CME Futures Hours (Central Time):
 * - Sunday: 5:00 PM - Friday 4:00 PM (continuous with breaks)
 * - Daily break: 4:00 PM - 5:00 PM CT
 * - Weekend: Friday 4:00 PM - Sunday 5:00 PM CT
 *
 * This uses UTC time for calculations
 */

export interface MarketHoursInfo {
  isOpen: boolean;
  status: 'OPEN' | 'CLOSED' | 'PRE_OPEN' | 'POST_CLOSE';
  nextOpen?: Date;
  nextClose?: Date;
  session?: 'REGULAR' | 'EXTENDED';
}

/**
 * Check if the market is currently open
 * ES/NQ futures trade nearly 24/5
 */
export function isMarketOpen(date: Date = new Date()): boolean {
  const info = getMarketHoursInfo(date);
  return info.isOpen;
}

/**
 * Get detailed market hours information
 */
export function getMarketHoursInfo(date: Date = new Date()): MarketHoursInfo {
  // Convert to Chicago time (UTC-6 or UTC-5 depending on DST)
  const chicagoTime = new Date(date.toLocaleString('en-US', { timeZone: 'America/Chicago' }));

  const dayOfWeek = chicagoTime.getDay(); // 0 = Sunday, 6 = Saturday
  const hours = chicagoTime.getHours();
  const minutes = chicagoTime.getMinutes();
  const timeInMinutes = hours * 60 + minutes;

  // Market is closed on Saturday
  if (dayOfWeek === 6) {
    return {
      isOpen: false,
      status: 'CLOSED',
      nextOpen: getNextSundayOpen(chicagoTime),
    };
  }

  // Sunday: Opens at 5:00 PM CT (1700)
  if (dayOfWeek === 0) {
    const sundayOpen = 17 * 60; // 5:00 PM in minutes

    if (timeInMinutes < sundayOpen) {
      return {
        isOpen: false,
        status: 'CLOSED',
        nextOpen: getNextSundayOpen(chicagoTime),
      };
    } else {
      return {
        isOpen: true,
        status: 'OPEN',
        session: 'EXTENDED',
        nextClose: getNextDailyClose(chicagoTime),
      };
    }
  }

  // Monday - Thursday: Daily break from 4:00 PM - 5:00 PM
  if (dayOfWeek >= 1 && dayOfWeek <= 4) {
    const dailyClose = 16 * 60; // 4:00 PM
    const dailyOpen = 17 * 60;  // 5:00 PM

    if (timeInMinutes >= dailyClose && timeInMinutes < dailyOpen) {
      return {
        isOpen: false,
        status: 'CLOSED',
        nextOpen: getTodayReopenTime(chicagoTime),
      };
    } else {
      return {
        isOpen: true,
        status: 'OPEN',
        session: 'EXTENDED',
        nextClose: getNextDailyClose(chicagoTime),
      };
    }
  }

  // Friday: Closes at 4:00 PM CT
  if (dayOfWeek === 5) {
    const fridayClose = 16 * 60; // 4:00 PM

    if (timeInMinutes >= fridayClose) {
      return {
        isOpen: false,
        status: 'CLOSED',
        nextOpen: getNextSundayOpen(chicagoTime),
      };
    } else {
      return {
        isOpen: true,
        status: 'OPEN',
        session: 'EXTENDED',
        nextClose: getTodayClose(chicagoTime),
      };
    }
  }

  // Default: closed
  return {
    isOpen: false,
    status: 'CLOSED',
  };
}

/**
 * Get next Sunday 5:00 PM CT opening time
 */
function getNextSundayOpen(currentTime: Date): Date {
  const result = new Date(currentTime);
  const currentDay = result.getDay();

  // Days until Sunday (0)
  let daysUntilSunday = (7 - currentDay) % 7;
  if (daysUntilSunday === 0 && result.getHours() >= 17) {
    daysUntilSunday = 7; // Next week
  }

  result.setDate(result.getDate() + daysUntilSunday);
  result.setHours(17, 0, 0, 0); // 5:00 PM

  return result;
}

/**
 * Get today's 5:00 PM reopen time
 */
function getTodayReopenTime(currentTime: Date): Date {
  const result = new Date(currentTime);
  result.setHours(17, 0, 0, 0); // 5:00 PM
  return result;
}

/**
 * Get today's 4:00 PM close time
 */
function getTodayClose(currentTime: Date): Date {
  const result = new Date(currentTime);
  result.setHours(16, 0, 0, 0); // 4:00 PM
  return result;
}

/**
 * Get next daily close time (4:00 PM today or tomorrow)
 */
function getNextDailyClose(currentTime: Date): Date {
  const result = new Date(currentTime);
  const currentHour = result.getHours();

  if (currentHour >= 16) {
    // After 4 PM, next close is tomorrow at 4 PM
    result.setDate(result.getDate() + 1);
  }

  result.setHours(16, 0, 0, 0); // 4:00 PM
  return result;
}

/**
 * Get market status string for display
 */
export function getMarketStatusString(date: Date = new Date()): string {
  const info = getMarketHoursInfo(date);

  if (info.isOpen) {
    return 'OPEN';
  } else if (info.nextOpen) {
    const now = date.getTime();
    const opensIn = info.nextOpen.getTime() - now;
    const hoursUntilOpen = Math.floor(opensIn / (1000 * 60 * 60));
    const minutesUntilOpen = Math.floor((opensIn % (1000 * 60 * 60)) / (1000 * 60));

    if (hoursUntilOpen < 1) {
      return `CLOSED (opens in ${minutesUntilOpen}m)`;
    } else if (hoursUntilOpen < 24) {
      return `CLOSED (opens in ${hoursUntilOpen}h ${minutesUntilOpen}m)`;
    } else {
      const days = Math.floor(hoursUntilOpen / 24);
      const remainingHours = hoursUntilOpen % 24;
      return `CLOSED (opens in ${days}d ${remainingHours}h)`;
    }
  }

  return 'CLOSED';
}
